import type {
  CompanyCardDto,
  EquipmentDto,
  EquipmentObligation,
  EquipmentState,
  EquipmentStatus,
  Site,
} from '@bk/contracts';
import { ownerError } from '@bk/contracts';
import { nobodyWorks } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type InputHTMLAttributes } from 'react';
import { api, errorMessage } from '../../../lib/api';
import { useTenant, useTenantQuery } from '../../../lib/useTenant';
import { Button, Card, Empty, ErrorText, Field } from '../../../ui';
import { useSlideIn } from './SitesStep';
import { BrainNotes } from './StepReview';
import { LibrarySources } from './LibrarySources';
import { DoneBand, Feeds, Glance, Notice, Pill, Row, RowGroup, useRows } from './Summary';

/** Un gruppo di impianti: una sede, oppure l'impresa per quelli non legati a una sede. */
interface Group {
  id: string | null;
  name: string;
  where: string;
}

/** Le attività economiche per id, con il codice ATECO: servono a dire da dove nasce ogni impianto. */
type Activities = Map<string, { nome: string; ateco: string | null }>;

/**
 * Impianti e attrezzature (ADR 0023, 0031): l'hardware dell'azienda, subito dopo sedi e attività da cui si ricava. Una
 * sede alla volta, un impianto alla volta: ogni scheda dice da dove nasce (sede → attività → impianto) e cosa comporta,
 * e si risponde «C'è», «Non c'è» o «Non so»; risposto, si passa subito al prossimo, senza aspettare il server. Quando
 * nessuno è più da confermare le schede lasciano il posto al riepilogo per sede, dove si completano marca, modello e
 * libretto. «Rivedi il passo» ripercorre le schede una per una con le risposte già date, e alla fine torna al riepilogo.
 */
export function EquipmentStep() {
  const { t, tenant, isManager } = useTenant();
  const qc = useQueryClient();
  const state = useTenantQuery<EquipmentState>(['equipment'], '/company/equipment', {
    refetchInterval: (d) =>
      d?.in_preparazione.length ||
      d?.icone_in_corso ||
      d?.in_completamento?.length ||
      d?.data.some(
        (x) => x.stato === 'presente' && x.marca && x.modello && x.libretto_stato === 'non_cercato',
      )
        ? 3000
        : false,
  });
  const sites = useTenantQuery<{ data: Site[] }>(['sites'], '/sites');
  const card = useTenantQuery<CompanyCardDto>(['company'], '/company');
  const list = state.data?.data ?? [];
  const open = (sites.data?.data ?? []).filter((s) => s.status !== 'chiusa');
  const preparing = new Set(state.data?.in_preparazione ?? []);
  // Solo i luoghi dove l'azienda lavora (ADR 0032): una sede che è solo un indirizzo non ha hardware, e una sede da cui
  // non si ricava nulla non si mostra finché non ha impianti.
  const working = open.filter((s) => !nobodyWorks(s.use, s.employees));
  const groups: Group[] = [
    ...open
      .filter(
        (s) =>
          list.some((x) => x.sede === s.id) || preparing.has(s.id) || !!state.data?.errori[s.id],
      )
      .map((s) => ({
        id: s.id,
        name: s.name,
        where: [s.municipality, s.province].filter(Boolean).join(' '),
      })),
    ...(list.some((x) => !x.sede)
      ? [{ id: null, name: 'Impresa', where: 'non legati a una sede' }]
      : []),
  ];
  const activities: Activities = new Map(
    (card.data?.attivita ?? []).map((a) => [a.id, { nome: a.nome, ateco: a.ateco }]),
  );
  const of = (g: Group) => list.filter((x) => x.sede === g.id);
  const pending = (g: Group) => of(g).filter((x) => x.stato === 'atteso');
  // Una sede si sta ricavando la prima volta (ancora nessun impianto) o si ricontrolla dopo le risposte: solo la prima
  // ferma il riepilogo. Il ricontrollo lavora in silenzio e, se aggiunge impianti da confermare, le schede tornano da sé.
  const deriving = (g: Group) => !!g.id && preparing.has(g.id) && !of(g).length;
  const checking = (g: Group) => !!g.id && preparing.has(g.id) && !!of(g).length;
  const finished =
    !!list.length && !list.some((x) => x.stato === 'atteso') && !groups.some(deriving);
  // A risposte finite il riepilogo al posto delle schede (ADR 0033). «Rivedi il passo» rimette le schede.
  const [review, setReview] = useState(false);
  const summary = finished && !review;

  // La sede aperta: quella scelta, altrimenti la prima con impianti da confermare. L'impianto aperto: quello scelto
  // (anche già risposto, per cambiare risposta), altrimenti il primo da confermare della sede; rivedendo il passo, il
  // primo della sede.
  const [siteKey, setSiteKey] = useState<string | undefined>();
  const [pick, setPick] = useState<string | null>(null);
  const [dir, setDir] = useState(1);
  const keyOf = (g: Group) => g.id ?? 'impresa';
  const group =
    groups.find((g) => keyOf(g) === siteKey) ?? groups.find((g) => pending(g).length) ?? groups[0];
  const items = group ? of(group) : [];
  const current =
    (pick && items.find((x) => x.id === pick)) ||
    (group ? pending(group)[0] : undefined) ||
    (review ? items[0] : undefined);

  /** Dopo una risposta: il prossimo impianto, la prossima sede, o il riepilogo a passo rivisto. */
  const advance = (x: EquipmentDto) => {
    setDir(1);
    if (!group) return;
    const left = (g: Group) => pending(g).filter((y) => y.id !== x.id);
    if (review) {
      const next = items[items.indexOf(x) + 1];
      if (next) return setPick(next.id);
      const after = groups[groups.indexOf(group) + 1];
      if (after) {
        setSiteKey(keyOf(after));
        return setPick(of(after)[0]?.id ?? null);
      }
      setPick(null);
      if (!groups.some((g) => left(g).length)) setReview(false);
      return;
    }
    setPick(null);
    if (!left(group).length) {
      // Sede finita: si passa alla prossima con impianti da confermare.
      const next = groups.find((g) => g !== group && left(g).length);
      if (next) setSiteKey(keyOf(next));
    }
  };

  const answerKey = ['equipment-answer', tenant.id];
  const equipmentKey = ['tenant', tenant.id, 'equipment'];
  const answer = useMutation({
    mutationKey: answerKey,
    mutationFn: ({ x, stato }: { x: EquipmentDto; stato: EquipmentStatus }) =>
      api<EquipmentDto>(t(`/company/equipment/${x.id}`), {
        method: 'PATCH',
        body: { stato },
      }),
    onMutate: async ({ x, stato }) => {
      // La risposta si vede subito e si passa al prossimo impianto, senza attese. Una lettura in corso non deve
      // riportare indietro la risposta appena data.
      advance(x);
      await qc.cancelQueries({ queryKey: equipmentKey });
      qc.setQueryData<EquipmentState>(equipmentKey, (old) =>
        old ? { ...old, data: old.data.map((y) => (y.id === x.id ? { ...y, stato } : y)) } : old,
      );
    },
    // Si rilegge dal server solo dopo l'ultima risposta di una raffica, così le schede non saltano indietro.
    onSettled: () => {
      if (qc.isMutating({ mutationKey: answerKey }) <= 1)
        void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
    },
  });
  const onAnswer = (x: EquipmentDto, stato: EquipmentStatus) =>
    // Rivedendo il passo, la risposta già data si conferma e si va avanti senza scrivere nulla.
    x.stato === stato ? advance(x) : answer.mutate({ x, stato });

  // Le domande del cervello (ADR 0065): della sede aperta mentre si risponde, di tutte nel riepilogo. Solo dai
  // riferimenti pronti: mentre una sede si prepara le sue non si mostrano, e BrainNotes dice che le sta rivedendo.
  const refreshing = (g: Group) => !!g.id && preparing.has(g.id);
  const questionsOf = (g: Group) => (refreshing(g) ? undefined : state.data?.domande?.[g.id ?? 'azienda']);
  const visible = summary ? groups : group ? [group] : [];
  const ready = visible.map(questionsOf).filter((q) => !!q);
  // «aggiornato» dice a BrainNotes che il riferimento è pronto, per la riga «Nessuna domanda».
  const notes = ready.length ? { domande: ready.flat(), aggiornato: 'pronto' } : null;
  const rows = useRows();

  const nSites = working.length;
  const nActivities = card.data?.attivita.length ?? 0;
  const answered = (g: Group) => of(g).length - pending(g).length;
  return (
    <div className="space-y-4">
      {!summary && !review && (
        <Card className="p-4 sm:p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-accent">
            🔧 L’hardware della tua azienda
          </p>
          <p className="mt-1 text-sm text-ink/75">
            Sai <b>dove</b> lavorate ({nSites} {nSites === 1 ? 'sede' : 'sedi'}) e <b>cosa</b> fate (
            {nActivities} attività). Ora vediamo <b>con cosa</b> lo fate: impianti, macchine e
            attrezzature. È ciò che si tocca, si guasta e va controllato: da qui nascono verifiche,
            manutenzioni e registri.
          </p>
        </Card>
      )}

      <BrainNotes
        step="impianti"
        d={notes}
        what={summary ? 'gli impianti' : 'gli impianti di questa sede'}
        rethinking={visible.some(refreshing)}
      />

      {!groups.length && (
        <Empty>
          {state.isLoading
            ? 'Carico gli impianti…'
            : 'Prima servono le sedi con le loro attività economiche: gli impianti si ricavano da lì.'}
        </Empty>
      )}

      {summary ? (
        <Inventory
          groups={groups}
          of={of}
          activities={activities}
          checking={checking}
          onReview={
            isManager
              ? () => {
                  setReview(true);
                  setSiteKey(groups[0] && keyOf(groups[0]));
                  setPick(groups[0] ? (of(groups[0])[0]?.id ?? null) : null);
                  setDir(1);
                }
              : null
          }
        />
      ) : (
        group && (
          <>
            {review && (
              <div className="flex items-center justify-between gap-2 px-1">
                <p className="text-sm text-ink/60">
                  Rivedi le risposte: tocca quella giusta e passi al prossimo.
                </p>
                {finished && (
                  <button
                    className="shrink-0 text-sm font-medium text-accent hover:underline"
                    onClick={() => (setReview(false), setPick(null))}
                  >
                    Torna al riepilogo
                  </button>
                )}
              </div>
            )}
            {groups.length > 1 && (
              <div className="flex flex-wrap gap-2" role="tablist" aria-label="Sedi">
                {groups.map((g) => {
                  const left = pending(g).length;
                  const here = g === group;
                  return (
                    <button
                      key={keyOf(g)}
                      role="tab"
                      aria-selected={here}
                      onClick={() => {
                        setSiteKey(keyOf(g));
                        setPick(null);
                      }}
                      className={`rounded-full px-3 py-1.5 text-sm transition ${here ? 'bg-accent text-white' : 'bg-white ring-1 ring-black/10 hover:ring-accent/40'}`}
                    >
                      📍 {g.name}{' '}
                      <span className={`text-xs ${here ? 'text-white/80' : 'text-ink/50'}`}>
                        {deriving(g) ? '…' : left ? `${answered(g)}/${of(g).length}` : '✓'}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            <Card className="p-4">
              <div className="mb-3 flex flex-wrap items-baseline gap-2">
                <h3 className="font-semibold">📍 {group.name}</h3>
                <span className="text-xs text-ink/50">{group.where}</span>
              </div>
              {deriving(group) && (
                <p className="mb-2 flex items-center gap-2 text-sm text-accent">
                  <Pulse /> Sto ricavando gli impianti di questa sede dalle sue attività…
                </p>
              )}
              {group.id && state.data?.errori[group.id] && (
                <p className="mb-2 text-sm text-red-700">
                  Non sono riuscito a ricavarli: {ownerError(state.data.errori[group.id]!)}
                </p>
              )}
              {!items.length && !deriving(group) && (
                <p className="text-sm text-ink/50">
                  Nessun impianto ancora: servono le attività economiche della sede.
                </p>
              )}

              {!!items.length && (
                <div
                  className="mb-3 flex flex-wrap items-center gap-1.5"
                  aria-label={`${answered(group)} di ${items.length} con risposta`}
                >
                  {items.map((x) => (
                    <button
                      key={x.id}
                      title={x.nome}
                      aria-label={`${x.nome} (${STATE_WORD[x.stato]})`}
                      aria-current={current?.id === x.id ? 'true' : undefined}
                      onClick={() => {
                        setDir(current && items.indexOf(x) < items.indexOf(current) ? -1 : 1);
                        setPick(x.id);
                      }}
                      className={`h-2 w-6 rounded-full transition-colors ${DOT[x.stato]} ${current?.id === x.id ? 'ring-2 ring-accent/50 ring-offset-1' : ''}`}
                    />
                  ))}
                  <span className="ml-2 text-xs tabular-nums text-ink/55">
                    {answered(group)} di {items.length}
                  </span>
                  {checking(group) && (
                    <span className="ml-auto flex items-center gap-1.5 text-xs text-ink/45">
                      <Pulse /> guardo se serve altro
                    </span>
                  )}
                </div>
              )}

              {current ? (
                <EquipmentCard
                  key={current.id}
                  x={current}
                  site={group}
                  activities={activities}
                  dir={dir}
                  busy={answer.isPending && answer.variables?.x.id === current.id}
                  onAnswer={(stato) => onAnswer(current, stato)}
                />
              ) : (
                !!items.length && (
                  <>
                    <p className="mb-1 rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                      ✓ Questa sede è a posto.
                    </p>
                    <SiteRows g={group} of={of} activities={activities} rows={rows} />
                  </>
                )
              )}
              <ErrorText>{answer.error ? errorMessage(answer.error) : ''}</ErrorText>
              {current && <AddEquipment siteId={group.id} />}
            </Card>
          </>
        )
      )}
      <p className="text-xs text-ink/45">
        Gli impianti che ci sono entrano nei documenti obbligatori di ogni sede (verifiche,
        dichiarazioni, registri).
      </p>
    </div>
  );
}

/** Il segno che il cervello sta lavorando, discreto: un punto che pulsa. */
function Pulse() {
  return <span aria-hidden className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />;
}

const STATE_WORD: Record<EquipmentStatus, string> = {
  atteso: 'supposto, da confermare',
  presente: 'c’è',
  non_presente: 'non c’è',
  da_verificare: 'non so, da verificare',
};
const DOT: Record<EquipmentStatus, string> = {
  atteso: 'bg-black/10',
  presente: 'bg-emerald-500',
  non_presente: 'bg-black/25',
  da_verificare: 'bg-amber-400',
};

/**
 * L'icona di un impianto: la sceglie il motore per ogni impianto (ADR 0035), idonea alla cosa e coerente con le altre.
 * Finché non c'è, un segno neutro: meglio niente che un'icona sbagliata.
 */
function EquipmentIcon({ x }: { x: EquipmentDto }) {
  return x.icona ? (
    <span aria-hidden>{x.icona}</span>
  ) : (
    <span aria-hidden className="inline-block h-2 w-2 rounded-full bg-black/15 align-middle" />
  );
}

/**
 * Da dove nasce un impianto: la sede, l'attività economica (con il codice ATECO), l'impianto. Le attività sono una
 * accanto all'altra, non una catena: con più di due si legge la prima e quante altre, e il resto sta nel suggerimento.
 */
function Chain({ x, site, activities }: { x: EquipmentDto; site: Group; activities: Activities }) {
  const acts = x.attivita.map((a) => activities.get(a)).filter((a) => !!a);
  const named = (a: { nome: string; ateco: string | null }) =>
    a.ateco ? `${a.nome} (${a.ateco})` : a.nome;
  const label =
    acts.length <= 2
      ? acts.map(named).join(' · ')
      : `${named(acts[0]!)} e altre ${acts.length - 1} attività`;
  return (
    <p className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-xs text-ink/55">
      <span>📍 {site.name}</span>
      {!!acts.length && (
        <span title={acts.map(named).join('\n')}>→ 🏷️ {label}</span>
      )}
      <span>
        → <EquipmentIcon x={x} /> {x.nome}
      </span>
    </p>
  );
}

/** Un impianto da confermare, in grande: perché te lo chiedo, cosa comporta, e tre risposte. */
function EquipmentCard({
  x,
  site,
  activities,
  dir,
  busy,
  onAnswer,
}: {
  x: EquipmentDto;
  site: Group;
  activities: Activities;
  dir: number;
  busy: boolean;
  onAnswer: (stato: EquipmentStatus) => void;
}) {
  const box = useSlideIn(dir);
  const [open, setOpen] = useState(false);
  const choice = (stato: EquipmentStatus, label: string, primary = false) => {
    const chosen = x.stato === stato;
    return (
      <Button
        variant={primary && x.stato === 'atteso' ? 'primary' : 'ghost'}
        className={
          chosen
            ? 'bg-emerald-50 text-emerald-900 ring-2 ring-emerald-400/70'
            : primary && x.stato === 'atteso'
              ? ''
              : 'ring-1 ring-black/10'
        }
        aria-pressed={chosen}
        disabled={busy}
        onClick={() => onAnswer(stato)}
      >
        {label}
        {chosen && ' ✓'}
      </Button>
    );
  };
  return (
    <div ref={box} className="rounded-2xl border-2 border-accent/20 bg-white p-4">
      <Chain x={x} site={site} activities={activities} />
      <p className="mt-2 text-lg font-semibold">
        <EquipmentIcon x={x} /> {x.nome}{' '}
        {x.tipo && <span className="text-sm font-normal text-ink/50">· {x.tipo}</span>}
      </p>
      {x.perche && (
        <p className="mt-2 text-sm text-ink/70">
          <span className="text-ink/45">Perché te lo chiedo: </span>
          {x.perche}
        </p>
      )}
      <LibrarySources refs={x.libreria} fondata={x.fondata} />
      {x.condizione && <p className="mt-1 text-sm text-amber-700">Solo se: {x.condizione}</p>}
      {x.stima && (
        <p className="mt-1 text-sm text-ink/70">
          <span className="text-ink/45">Quanti: </span>
          {x.stima} <span className="text-xs text-ink/45">(stima, la correggi dopo)</span>
        </p>
      )}
      {!!x.obblighi.length && (
        <div className="mt-2 text-sm">
          <button className="text-accent hover:underline" onClick={() => setOpen(!open)}>
            Il registro: {x.obblighi.length} {x.obblighi.length === 1 ? 'cosa da fare' : 'cose da fare'}
            {open ? ' ▴' : ' ▾'}
          </button>
          {open && <Obligations x={x} />}
        </div>
      )}
      <p className="mt-4 text-sm font-medium">Ce l’avete in questa sede?</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {choice('presente', 'C’è', true)}
        {choice('non_presente', 'Non c’è')}
        {choice('da_verificare', 'Non so')}
      </div>
      <p className="mt-2 text-xs text-ink/45">
        «Non so» va bene: lo segno da verificare con chi lo sa, per esempio il manutentore.
      </p>
    </div>
  );
}

/** Perché c'è una riga del registro (ADR 0134): la legge o la buona prassi, con la loro fonte accanto. */
function Basis({ o }: { o: EquipmentObligation }) {
  const good = o.base === 'buona_prassi';
  return (
    <span
      className={`inline-block rounded-full px-1.5 py-px text-[11px] font-semibold ${good ? 'bg-emerald-50 text-emerald-800' : 'bg-orange-50 text-orange-800'}`}
    >
      {good ? 'Buona prassi' : 'Obbligatorio'}
    </span>
  );
}

/** Le righe del registro di un impianto: cosa si fa, ogni quanto, chi, la prova e perché. */
function Obligations({ x }: { x: EquipmentDto }) {
  return (
    <ul className="mt-1 space-y-1 text-xs">
      {x.obblighi.map((o, i) => (
        <li key={i} className="rounded bg-black/[0.03] px-2 py-1.5">
          <p>
            <Basis o={o} /> <b>{o.descrizione}</b>
          </p>
          <p className="mt-0.5 text-ink/60">
            {[o.periodicita, o.chi, o.prova && `prova: ${o.prova}`].filter(Boolean).join(' · ')}
          </p>
          <p className="text-ink/45">
            {o.norma}
            {o.fonte === 'produttore' && ' · dal libretto'}
          </p>
        </li>
      ))}
    </ul>
  );
}

/**
 * Il registro della sede (ADR 0134): una riga per ogni cosa da fare sugli impianti che ci sono o sono da verificare,
 * con quanti sono (detto dal titolare o stimato), perché, cosa si fa, ogni quanto e quale ruolo lo fa.
 */
function Register({ list }: { list: EquipmentDto[] }) {
  const rows = list.flatMap((x) => x.obblighi.map((o, i) => ({ x, o, first: i === 0, span: x.obblighi.length })));
  if (!rows.length) return null;
  const law = rows.filter((r) => r.o.base !== 'buona_prassi').length;
  return (
    <details className="mt-2 rounded-2xl bg-white ring-1 ring-black/5" open>
      <summary className="cursor-pointer px-4 py-2.5 text-sm font-medium">
        Il registro della sede{' '}
        <span className="font-normal text-ink/50">
          · {rows.length} {rows.length === 1 ? 'cosa da fare' : 'cose da fare'}, {law} per legge
        </span>
      </summary>
      <div className="overflow-x-auto border-t border-black/5">
        <table className="w-full min-w-[46rem] text-left text-xs">
          <thead className="text-[11px] uppercase tracking-wide text-ink/45">
            <tr>
              <th className="px-3 py-2 font-semibold">Impianto</th>
              <th className="px-3 py-2 font-semibold">Quanti</th>
              <th className="px-3 py-2 font-semibold">Perché</th>
              <th className="px-3 py-2 font-semibold">Cosa si fa</th>
              <th className="px-3 py-2 font-semibold">Ogni quanto</th>
              <th className="px-3 py-2 font-semibold">Chi</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ x, o, first, span }, k) => (
              <tr key={`${x.id}:${k}`} className="border-t border-black/5 align-top">
                {first && (
                  <td rowSpan={span} className="px-3 py-2 font-semibold">
                    <EquipmentIcon x={x} /> {x.nome}
                  </td>
                )}
                {first && (
                  <td rowSpan={span} className="px-3 py-2 tabular-nums">
                    {x.quantita ?? x.stima ?? '—'}
                    {!x.quantita && x.stima && <span className="block text-[11px] text-ink/45">stima</span>}
                  </td>
                )}
                <td className="px-3 py-2">
                  <Basis o={o} />
                  <span className="mt-0.5 block text-ink/55">{o.norma}</span>
                </td>
                <td className="px-3 py-2">
                  {o.descrizione}
                  {o.prova && <span className="block text-ink/45">prova: {o.prova}</span>}
                </td>
                <td className="px-3 py-2">{o.periodicita ?? '—'}</td>
                <td className="px-3 py-2">{o.chi ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

/**
 * A risposte finite, il riepilogo (ADR 0033): la fascia verde, i numeri in breve, cosa resta da verificare e, sede per
 * sede, una riga per impianto con la sua risposta. «Modifica» apre l'impianto da solo: si cambia la risposta e, se c'è,
 * si completano quanti, marca e modello per avere il libretto.
 */
function Inventory({
  groups,
  of,
  activities,
  checking,
  onReview,
}: {
  groups: Group[];
  of: (g: Group) => EquipmentDto[];
  activities: Activities;
  checking: (g: Group) => boolean;
  onReview: (() => void) | null;
}) {
  const rows = useRows();
  const all = groups.flatMap(of);
  const here = all.filter((x) => x.stato === 'presente');
  const unsure = all.filter((x) => x.stato === 'da_verificare');
  const duties = here.reduce((n, x) => n + x.obblighi.length, 0);
  const byLaw = here.reduce((n, x) => n + x.obblighi.filter((o) => o.base !== 'buona_prassi').length, 0);
  return (
    <div>
      <DoneBand
        title="L’attrezzatura è a posto"
        text="Hai finito l’azienda fisica: dove lavora, cosa fa e con cosa. Più avanti vediamo come funziona."
        onReview={onReview}
      />
      <Glance
        tiles={[
          [here.length, here.length === 1 ? 'impianto presente' : 'impianti presenti'],
          unsure.length > 0 && [unsure.length, 'da verificare'],
          duties > 0 && [duties, duties === 1 ? 'cosa da fare nel registro' : 'cose da fare nel registro'],
          byLaw > 0 && [byLaw, byLaw === 1 ? 'chiesta dalla legge' : 'chieste dalla legge'],
        ]}
      />
      {unsure.length > 0 && (
        <Notice>
          Restano da verificare: <b>{unsure.map((x) => x.nome).join(', ')}</b>. Non blocca nulla:
          chiedilo a chi lo sa, per esempio il manutentore, e poi cambia la risposta qui.
        </Notice>
      )}
      {groups.map((g) => (
        <SiteRows
          key={g.id ?? 'impresa'}
          g={g}
          of={of}
          activities={activities}
          rows={rows}
          checking={checking(g)}
          titled
        />
      ))}
      <Feeds steps={['Organizzazione e ruoli', 'La mappa dell’azienda', 'Procedure', 'Documenti e collegamenti']} />
    </div>
  );
}

// Prima ciò che c'è, poi il da verificare; ciò che non c'è sta in una nota in fondo.
const ORDER: Record<EquipmentStatus, number> = {
  presente: 0,
  da_verificare: 1,
  atteso: 2,
  non_presente: 3,
};

/** Gli impianti di una sede come righe: «Modifica» apre l'impianto al suo posto, il resto resta fermo. */
function SiteRows({
  g,
  of,
  activities,
  rows,
  checking = false,
  titled = false,
}: {
  g: Group;
  of: (g: Group) => EquipmentDto[];
  activities: Activities;
  rows: ReturnType<typeof useRows>;
  checking?: boolean;
  titled?: boolean;
}) {
  const { isManager } = useTenant();
  // Gli impianti aggiunti dal titolare che il cervello sta completando (ADR 0098), dallo stesso stato della pagina.
  const state = useTenantQuery<EquipmentState>(['equipment'], '/company/equipment');
  const completing = new Set(state.data?.in_completamento ?? []);
  const list = of(g)
    .filter((x) => x.stato === 'presente' || x.stato === 'da_verificare')
    .sort((a, b) => ORDER[a.stato] - ORDER[b.stato]);
  const gone = of(g).filter((x) => x.stato === 'non_presente');
  const n = list.filter((x) => x.stato === 'presente').length;
  const body = (
    <>
      {list.map((x) => (
        <Row
          key={x.id}
          icon={<EquipmentIcon x={x} />}
          open={rows.open === x.id}
          edited={rows.edited === x.id}
          onEdit={isManager ? () => rows.toggle(x.id) : null}
          editor={
            <EquipmentEdit x={x} site={g} activities={activities} onAnswered={() => rows.close(true)} />
          }
        >
          <p className="font-medium">
            {x.nome}
            <Pill tone={x.stato === 'presente' ? 'green' : 'amber'}>{STATE_PILL[x.stato]}</Pill>
          </p>
          {x.stato === 'presente' && (
            <p className="text-xs text-ink/45">
              {[
                x.quantita ? `${x.quantita} in tutto` : x.stima ? `${x.stima} (stima)` : null,
                [x.marca, x.modello].filter(Boolean).join(' ') || 'marca e modello da dire',
                x.fonte === 'persona' ? 'aggiunto da te' : null,
                x.libretto_stato === 'trovato' ? 'libretto trovato' : null,
                x.obblighi.length
                  ? `${x.obblighi.length} ${x.obblighi.length === 1 ? 'cosa da fare' : 'cose da fare'}`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
          {completing.has(x.id) && (
            <p className="flex items-center gap-1.5 text-xs text-ink/45">
              <Pulse /> lo completo: a cosa serve e cosa comporta
            </p>
          )}
        </Row>
      ))}
      {isManager && (
        <Row
          icon="＋"
          open={rows.open === `nuovo:${g.id ?? 'impresa'}`}
          onEdit={() => rows.toggle(`nuovo:${g.id ?? 'impresa'}`)}
          editLabel="Aggiungi"
          editor={<AddEquipment siteId={g.id} />}
        >
          <p className="text-sm text-ink/55">Manca un impianto in questa sede?</p>
        </Row>
      )}
    </>
  );
  return (
    <>
      {titled ? (
        <RowGroup
          title={`📍 ${g.name}`}
          sub={
            <>
              {[g.where, `${n} ${n === 1 ? 'impianto' : 'impianti'}`].filter(Boolean).join(' · ')}
              {checking && (
                <span className="ml-2 inline-flex items-center gap-1.5 text-ink/45">
                  <span aria-hidden className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-accent" />
                  guardo se serve altro
                </span>
              )}
            </>
          }
        >
          {body}
        </RowGroup>
      ) : (
        <ul className="mt-2 divide-y divide-black/5 overflow-hidden rounded-2xl bg-white ring-1 ring-black/5">
          {body}
        </ul>
      )}
      {titled && <Register list={list} />}
      {gone.length > 0 && <Absent rows={gone} manager={isManager} />}
    </>
  );
}

/**
 * Ciò che la persona ha detto che non c'è: non è una riga del riepilogo, solo una nota in fondo alla sede. «Rimetti»
 * lo riporta da confermare, e il passo torna da sé alla sua scheda (ADR 0033).
 */
function Absent({ rows, manager }: { rows: EquipmentDto[]; manager: boolean }) {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const back = useMutation({
    mutationFn: (x: EquipmentDto) =>
      api(t(`/company/equipment/${x.id}`), { method: 'PATCH', body: { stato: 'atteso' } }),
    onMutate: (x) =>
      qc.setQueryData<EquipmentState>(['tenant', tenant.id, 'equipment'], (old) =>
        old
          ? { ...old, data: old.data.map((y) => (y.id === x.id ? { ...y, stato: 'atteso' } : y)) }
          : old,
      ),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] }),
  });
  return (
    <p className="mt-2 mb-4 px-1 text-xs text-ink/45">
      Non ci sono:{' '}
      {rows.map((x, k) => (
        <span key={x.id}>
          {k > 0 && ', '}
          {x.nome}
          {manager && (
            <>
              {' '}
              <button
                className="text-accent/80 hover:underline disabled:opacity-50"
                disabled={back.isPending}
                onClick={() => back.mutate(x)}
              >
                rimetti
              </button>
            </>
          )}
        </span>
      ))}
    </p>
  );
}

const STATE_PILL: Record<EquipmentStatus, string> = {
  atteso: 'supposto, da confermare',
  presente: 'c’è',
  non_presente: 'non c’è',
  da_verificare: 'da verificare',
};

/** Un impianto aperto dal riepilogo: la risposta da cambiare e, se c'è, i suoi dettagli. */
function EquipmentEdit({
  x,
  site,
  activities,
  onAnswered,
}: {
  x: EquipmentDto;
  site: Group;
  activities: Activities;
  onAnswered: () => void;
}) {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const key = ['tenant', tenant.id, 'equipment'];
  const answer = useMutation({
    mutationFn: (stato: EquipmentStatus) =>
      api<EquipmentDto>(t(`/company/equipment/${x.id}`), { method: 'PATCH', body: { stato } }),
    onMutate: async (stato) => {
      // Diventato «c'è»: resta aperto per marca e modello. Altrimenti torna subito riga, senza aspettare il server.
      if (stato !== 'presente') onAnswered();
      await qc.cancelQueries({ queryKey: key });
      qc.setQueryData<EquipmentState>(key, (old) =>
        old ? { ...old, data: old.data.map((y) => (y.id === x.id ? { ...y, stato } : y)) } : old,
      );
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] }),
  });
  const choices: Array<[EquipmentStatus, string]> = [
    ['presente', 'C’è'],
    ['non_presente', 'Non c’è'],
    ['da_verificare', 'Non so'],
  ];
  return (
    <div>
      <Chain x={x} site={site} activities={activities} />
      <p className="mt-1 font-semibold">
        <EquipmentIcon x={x} /> {x.nome}
      </p>
      {x.perche && <p className="text-sm text-ink/60">{x.perche}</p>}
      <LibrarySources refs={x.libreria} fondata={x.fondata} />
      <p className="mt-3 text-sm font-medium">Ce l’avete in questa sede?</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {choices.map(([stato, label]) => (
          <button
            key={stato}
            disabled={answer.isPending}
            aria-pressed={x.stato === stato}
            onClick={() => (x.stato === stato ? onAnswered() : answer.mutate(stato))}
            className={`rounded-xl border-2 px-4 py-2 text-sm font-medium transition disabled:opacity-60 ${x.stato === stato ? 'border-emerald-400/70 bg-emerald-50/60 text-emerald-900' : 'border-black/5 bg-white hover:border-accent/40'}`}
          >
            {label}
            {x.stato === stato && ' ✓'}
          </button>
        ))}
      </div>
      {x.stato === 'presente' && <EquipmentDetails x={x} />}
      <ErrorText>{answer.error ? errorMessage(answer.error) : ''}</ErrorText>
    </div>
  );
}

/**
 * Quanti, marca e modello di un impianto che c'è: si salvano da soli quando si esce dal campo (o con Invio), senza un
 * pulsante da ricordare. Con marca e modello il sistema cerca il libretto del produttore.
 */
function EquipmentDetails({ x }: { x: EquipmentDto }) {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const key = ['tenant', tenant.id, 'equipment'];
  const initial = {
    quantita: x.quantita?.toString() ?? '',
    marca: x.marca ?? '',
    modello: x.modello ?? '',
  };
  const [v, setV] = useState(initial);
  const [saved, setSaved] = useState(initial);
  const [open, setOpen] = useState(false);
  const save = useMutation({
    mutationFn: (body: { quantita: number | null; marca: string | null; modello: string | null }) =>
      api<EquipmentDto>(t(`/company/equipment/${x.id}`), { method: 'PATCH', body }),
    onSuccess: (row) => {
      // Solo l'impianto cambia: niente rilettura di tutta la pagina.
      qc.setQueryData<EquipmentState>(key, (old) =>
        old ? { ...old, data: old.data.map((y) => (y.id === row.id ? row : y)) } : old,
      );
      void qc.invalidateQueries({ queryKey: key });
    },
  });
  const commit = () => {
    const next = { quantita: v.quantita.trim(), marca: v.marca.trim(), modello: v.modello.trim() };
    if (JSON.stringify(next) === JSON.stringify(saved)) return;
    setSaved(next);
    save.mutate({
      quantita: next.quantita && Number.isInteger(Number(next.quantita)) ? Number(next.quantita) : null,
      marca: next.marca || null,
      modello: next.modello || null,
    });
  };
  const field = (k: keyof typeof v, label: string, extra: InputHTMLAttributes<HTMLInputElement> = {}) => (
    <Field
      label={label}
      value={v[k]}
      onChange={(e) => setV({ ...v, [k]: e.target.value })}
      onBlur={commit}
      onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
      {...extra}
    />
  );
  return (
    <div className="mt-3 rounded-xl border border-black/5 bg-white p-3 text-sm">
      <div className="grid grid-cols-[4.5rem_1fr_1fr] gap-2">
        {field('quantita', 'Quanti', { type: 'number', min: 0, inputMode: 'numeric' })}
        {field('marca', 'Marca')}
        {field('modello', 'Modello')}
      </div>
      <p className="mt-2 text-xs">
        {save.isPending ? (
          <span className="text-ink/50">Salvo…</span>
        ) : save.isSuccess && !save.error ? (
          <span className="text-emerald-700">✓ Salvato · </span>
        ) : null}
        {!save.isPending &&
          (x.libretto_stato === 'trovato' && x.libretto ? (
            <a className="text-accent hover:underline" href={x.libretto} target="_blank" rel="noreferrer">
              📘 Libretto del produttore
            </a>
          ) : x.libretto_stato === 'non_trovato' ? (
            <span className="text-ink/50">Libretto non trovato online</span>
          ) : x.libretto_stato === 'non_identificato' ? (
            <span className="text-amber-700">Prodotto non identificato: controlla marca e modello</span>
          ) : x.marca && x.modello ? (
            <span className="text-accent">Cerco il libretto del produttore…</span>
          ) : (
            <span className="text-ink/50">Con marca e modello cerco il libretto del produttore</span>
          ))}
        {!!x.manutenzione.length && (
          <span className="text-ink/55">
            {' '}
            · Manutenzione: {x.manutenzione.map((m) => `${m.attivita} (${m.cadenza})`).join('; ')}
          </span>
        )}
      </p>
      {!!x.obblighi.length && (
        <div className="mt-1">
          <button className="text-xs text-accent hover:underline" onClick={() => setOpen(!open)}>
            {open
              ? 'Nascondi il registro'
              : `Il registro: ${x.obblighi.length} ${x.obblighi.length === 1 ? 'cosa da fare' : 'cose da fare'}`}
          </button>
          {open && <Obligations x={x} />}
        </div>
      )}
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
    </div>
  );
}

function AddEquipment({ siteId }: { siteId: string | null }) {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const add = useMutation({
    mutationFn: () => api(t('/company/equipment'), { body: { nome: name.trim(), sede: siteId } }),
    onSuccess: () => {
      setName('');
      void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
    },
  });
  return (
    <form
      className="mt-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim() && !add.isPending) add.mutate();
      }}
    >
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Field
            label="Abbiamo anche…"
            placeholder="Un impianto, una macchina o un’attrezzatura che non ho previsto"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <Button type="submit" variant="ghost" className="ring-1 ring-black/10" disabled={!name.trim() || add.isPending}>
          {add.isPending ? 'Aggiungo…' : 'Aggiungi'}
        </Button>
      </div>
      <ErrorText>{add.error ? errorMessage(add.error) : ''}</ErrorText>
    </form>
  );
}
